import { useId, useMemo } from "react";
import {
  ghostBtnClass,
  mfgInlineInputClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { sanitizeSlotLabelInput } from "@/lib/manufacturing/slotLabel";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type { VariantDeltaLine } from "@/lib/manufacturing/variantConfig";
import {
  ManufacturingComboboxPicker,
  type ComboboxOption,
} from "./ManufacturingComboboxPicker";
import { ManufacturingMarkMaterialMissingControl } from "./ManufacturingMarkMaterialMissingControl";
import { ManufacturingNegativeAdjustmentsHeading } from "./ManufacturingNegativeAdjustmentsHeading";

function emptyDeltaLine(quantity = 1): VariantDeltaLine {
  return {
    material_id: null,
    material_code: "",
    material_name: "",
    quantity,
    slot_label: "",
  };
}

/** Author added and saved materials for a delta named-variant effect. */
export function ManufacturingNamedVariantDeltaLinesEditor({
  lines,
  materials,
  orgSlotLabels,
  onChange,
  namedVariantId,
  onError,
}: {
  lines: readonly VariantDeltaLine[];
  materials: readonly MfgMaterial[];
  orgSlotLabels: readonly string[];
  onChange: (next: VariantDeltaLine[]) => void;
  namedVariantId?: string | null;
  onError?: (message: string) => void;
}) {
  const listId = useId();
  const rows = [...lines];
  const materialOptions = useMemo<ComboboxOption<MfgMaterial>[]>(
    () =>
      materials.map((m) => ({
        key: m.id,
        label: m.code,
        sublabel: m.name,
        data: m,
      })),
    [materials],
  );

  function patch(index: number, next: Partial<VariantDeltaLine>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...next } : row)));
  }

  const indexed = rows.map((row, index) => ({ row, index }));
  const additions = indexed.filter((item) => !(item.row.quantity < 0));
  const credits = indexed.filter((item) => item.row.quantity < 0);

  function renderRow(row: VariantDeltaLine, index: number, sign: "positive" | "negative") {
    return (
        <div
          key={`${listId}-${index}`}
          className="space-y-2 rounded-md border border-gray-100 p-2 dark:border-zinc-700"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
              {sign === "negative" ? "Savings line" : "Delta line"}
            </span>
            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>
          {row.material_use_id && !row.material_id ? (
            <div className="rounded-md border border-dashed border-gray-200 px-2 py-1.5 text-xs text-gray-700 dark:border-zinc-700 dark:text-zinc-300">
              Part use:{" "}
              <span className="font-medium">
                {row.material_code || row.material_use_id}
              </span>
              {row.material_name ? ` — ${row.material_name}` : ""}
            </div>
          ) : (
            <div className="space-y-1">
              <ManufacturingComboboxPicker<MfgMaterial>
                value={row.material_id ?? ""}
                options={materialOptions}
                placeholder="Select material…"
                onPick={(opt) =>
                  patch(index, {
                    material_id: opt.data.id,
                    material_use_id: null,
                    material_code: opt.data.code,
                    material_name: opt.data.name,
                  })
                }
              />
              {namedVariantId && !row.material_id ? (
                <ManufacturingMarkMaterialMissingControl
                  ownerKind="named_variant"
                  namedVariantId={namedVariantId}
                  defaultName={row.material_name}
                  quantity={row.quantity}
                  slotLabel={row.slot_label}
                  onError={onError}
                  onCreated={(materialName) => {
                    patch(index, {
                      material_id: null,
                      material_code: "",
                      material_name: materialName,
                    });
                  }}
                />
              ) : null}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <label className="flex min-w-[88px] flex-col gap-1">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
                Qty
              </span>
              <input
                className={mfgInlineInputClass}
                type="number"
                step="any"
                min={sign === "positive" ? 0.0001 : undefined}
                max={sign === "negative" ? -0.0001 : undefined}
                value={row.quantity}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  const ok = sign === "negative" ? Number.isFinite(n) && n < 0 : Number.isFinite(n) && n > 0;
                  if (ok) patch(index, { quantity: n });
                }}
              />
            </label>
            <label className="flex min-w-[140px] flex-1 flex-col gap-1">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
                Slot label (optional)
              </span>
              <input
                className={mfgInlineInputClass}
                list={`${listId}-slot-${index}`}
                placeholder="e.g. joist"
                value={row.slot_label}
                onChange={(e) =>
                  patch(index, { slot_label: sanitizeSlotLabelInput(e.target.value) })
                }
              />
              <datalist id={`${listId}-slot-${index}`}>
                {orgSlotLabels.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </label>
          </div>
        </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-gray-500 dark:text-zinc-400">
        Add materials that this upgrade contributes on top of the parent BOM (e.g. extra joists and
        nails for 12&quot; OC). Parent lines are kept — nothing is replaced unless you use
        Transform mode.
      </p>
      {additions.length === 0 ? (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">No added materials yet.</p>
      ) : (
        additions.map(({ row, index }) => renderRow(row, index, "positive"))
      )}
      <button
        type="button"
        className={ghostBtnClass}
        onClick={() => onChange([...rows, emptyDeltaLine(1)])}
      >
        + Add delta line
      </button>
      <ManufacturingNegativeAdjustmentsHeading />
      {credits.length === 0 ? (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">No savings lines.</p>
      ) : (
        credits.map(({ row, index }) => renderRow(row, index, "negative"))
      )}
      <button
        type="button"
        className={ghostBtnClass}
        onClick={() => onChange([...rows, emptyDeltaLine(-1)])}
      >
        + Add savings line
      </button>
    </div>
  );
}
